import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import Button from "../../components/button/Button";
import { largeModalShowHide, setTitle } from "../../features/modalSlice";
import { LargeModal } from "../../components/modal/ModalUI";
import { getChallengeItemList, getChallengeList, setChallengeDetails } from "../../features/challengeSlice";
import ChallengeItems from "./ChallengeItems";
import ChallengeDetails from "./ChallengeDetails";
import { downloadExportedFile, exportChallengeData } from "../../apis/fileExport.api";
import DateInput from "../../components/form/DateInput";
import { errorToastify, successToastify } from "../../utils/toastify.util";

const Challenge = () => {
  const dispatch = useDispatch();
  const { challengeList } = useSelector((state) => state.challenge);

  const [modalBody, setModalBody] = useState(null);

  // Pagination State
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  // Search State
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Debounce helper
  const debouncer = useMemo(() => {
    let t;
    return (cb) => {
      clearTimeout(t);
      t = setTimeout(cb, 400);
      return () => clearTimeout(t);
    };
  }, []);

  // Fetch challenge list with pagination + search
  useEffect(() => {
    const run = () => {
      const payload = { offset, limit, searchText, startDate, endDate };
      dispatch(getChallengeList(payload));
    };
    return debouncer(run);
  }, [dispatch, offset, limit, searchText, debouncer, startDate, endDate]);

  // Reset to page 1 when search/limit/dates change
  useEffect(() => {
    setOffset(0);
  }, [searchText, limit, startDate, endDate]);

  const challengeMoreDetails = (i, body) => {
    const challenge = challengeList[i];
    if (body === "details") {
      dispatch(setChallengeDetails(challenge));
      dispatch(setTitle("Quest Details"));
    } else if (body === "items") {
      dispatch(getChallengeItemList({ challengeid: challenge.id }));
      dispatch(setTitle("Quest Challenges"));
    }
    setModalBody(body);
    dispatch(largeModalShowHide());
  };

  const setterModalBody = (arg) => {
    if (arg === "details") return <ChallengeDetails />;
    if (arg === "challenges") return <ChallengeItems />;
    return null;
  };

  // Pagination handlers
  const nextPage = () => {
    if (challengeList.length === limit) setOffset((prev) => prev + limit);
  };
  const prevPage = () => {
    setOffset((prev) => Math.max(0, prev - limit));
  };

  const currentPage = Math.floor(offset / limit) + 1;

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const payload = { searchText, startDate, endDate, offset: 0, limit: 10000 };
      const result = await exportChallengeData(payload);
      const filePath = result?.result?.filePath;
      if (!filePath) {
        throw new Error(result?.message || "Failed to generate quest export file");
      }
      await downloadExportedFile(filePath, "quests.xlsx");
      successToastify("Quest data exported successfully");
    } catch (error) {
      console.error("Export Error:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to export quest data";
      errorToastify(msg);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div id="main" className="main">
      <Breadcrumb page="Quest List" />
      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {/* Heading + Search (side-by-side) */}
                <div className="d-flex w-100 align-items-center gap-2 flex-wrap">
                  <h5 className="card-title mb-0">Quest List</h5>

                  <Button
                    className="btn btn-outline-primary btn-sm fs-7 ms-auto"
                    title="Export Quest List"
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-download"></i> Export
                      </>
                    )}
                  </Button>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                  <div>
                    <span className="ps-1 text-sm">Search Quest</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search Quest..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{ maxWidth: 280 }}
                    />
                  </div>

                  <div>
                    {/* Start Date */}
                    <span className=" ps-1 text-sm mb-1">Start date</span>
                    <DateInput
                      className="form-control"
                      style={{ maxWidth: 180 }}
                      value={startDate}
                      onChange={setStartDate}
                    />
                  </div>

                  <div>
                    {/* End Date */}
                    <span className="ps-1 text-sm mb-1">End date</span>
                    <DateInput
                      className="form-control"
                      style={{ maxWidth: 180 }}
                      value={endDate}
                      onChange={setEndDate}
                    />
                  </div>

                  {(startDate || endDate || searchText) && (
                    <div className="d-flex align-items-end">
                      <Button
                        className="btn btn-outline-secondary btn-sm fs-7"
                        onClick={() => {
                          setSearchText("");
                          setStartDate("");
                          setEndDate("");
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </div>

                <table className="table table-bordered mt-3">
                  <thead>
                    <tr>
                      <th className="text-center fs-7">Title</th>
                      <th className="text-center fs-7">Creator Name</th>
                      <th className="text-center fs-7">Creator Email</th>
                      <th className="text-center fs-7">Start Date</th>
                      <th className="text-center fs-7">End Date</th>
                      <th className="text-center fs-7">More Details</th>
                      <th className="text-center fs-7">Quest Challenges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {challengeList?.length ? (
                      challengeList.map((challenge, i) => (
                        <tr key={`challengeList-${i}`}>
                          <td className="text-center fs-7">{challenge?.name || null}</td>
                          <td className="text-center fs-7">{challenge?.username || null}</td>
                          <td className="text-center fs-7">{challenge?.email || null}</td>

                          <td className="text-center fs-7">{challenge?.createdat || null}</td>
                          <td className="text-center fs-7">{challenge?.endtime || null}</td>
                          <td className="text-center fs-7">
                            <Button
                              className="btn btn-outline-success btn-sm fs-7"
                              onClick={() => challengeMoreDetails(i, "details")}
                            >
                              <i className="fa-regular fa-eye"></i>
                            </Button>
                          </td>
                          <td className="text-center fs-7">
                            <Button
                              className="btn btn-outline-success btn-sm fs-7"
                              onClick={() => challengeMoreDetails(i, "items")}
                            >
                              <i className="fa-regular fa-eye"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No Quest Available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination + Limit selector */}
                <div className="d-flex justify-content-end align-items-center mt-3 gap-2 flex-wrap">
                  {/* Limit dropdown */}
                  <div className="d-inline-flex align-items-center gap-2">
                    <span className="fs-7">Show</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="fs-7">per page</span>
                  </div>

                  <div className="flex-grow-1" />

                  {/* Pagination buttons with page number in between */}
                  <Button className="btn btn-outline-primary btn-sm" onClick={prevPage} disabled={offset === 0}>
                    Previous
                  </Button>

                  <span className="px-2 fs-7">Page {currentPage}</span>

                  <Button
                    className="btn btn-outline-primary btn-sm"
                    onClick={nextPage}
                    disabled={challengeList.length < limit || challengeList.length === 0}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LargeModal>{setterModalBody(modalBody)}</LargeModal>
    </div>
  );
};

export default Challenge;
